import { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import useCreatePaymentMethod from "@hooks/billing/useCreatePaymentMethod";
import { ADD_PAYMENT_METHOD } from "@store/user/userActions";
import {
  Stripe,
  StripeElements,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from "@stripe/stripe-js";
import { PaymentMethodManagerView } from "@types-runway/billing";

interface UseLinkExpressCheckoutOptions {
  isPaymentInFlight: () => boolean;
  setPaymentInFlight: (value: boolean) => void;
  onPaymentMethodAttached: () => void;
  // ExpressCheckoutElement's "click" event has no prevent/cancel mechanism
  // (unlike PaymentRequestButtonElement's onClick), so the address check
  // that gates Apple Pay before its sheet opens has to happen here instead —
  // after Link's own auth completes, but before we tokenize anything.
  validateBeforeConfirm: () => boolean;
}

// The installed @stripe/stripe-js type definitions only declare applePay/
// googlePay on the ExpressCheckoutElement "ready" event, but the real
// runtime payload also reports link — narrowing here instead of casting to
// `any`, since the field genuinely exists at runtime (confirmed via Stripe's
// docs) even though this SDK version's types haven't caught up.
type ReadyEventAvailablePaymentMethods = StripeExpressCheckoutElementReadyEvent["availablePaymentMethods"] & {
  link?: boolean;
};

// Owns the Link-specific Stripe mechanics for the express "Pay with Link"
// button (ExpressCheckoutElement, restricted to Link only — Apple/Google Pay
// are suppressed here since we already render our own Apple Pay button).
// Mirrors useApplePay: CheckoutPage/PaymentMethodManager only see a ready
// button and a confirm handler, never elements.submit()/createPaymentMethod
// directly.
const useLinkExpressCheckout = (
  stripe: Stripe | null,
  elements: StripeElements | null,
  {
    isPaymentInFlight,
    setPaymentInFlight,
    onPaymentMethodAttached,
    validateBeforeConfirm,
  }: UseLinkExpressCheckoutOptions
) => {
  const dispatch = useDispatch();
  const { mapStripePaymentMethodToDTO } = useCreatePaymentMethod();

  const [canUseLink, setCanUseLink] = useState(false);

  const onPaymentMethodAttachedRef = useRef(onPaymentMethodAttached);
  onPaymentMethodAttachedRef.current = onPaymentMethodAttached;

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    const availablePaymentMethods = event.availablePaymentMethods as
      | ReadyEventAvailablePaymentMethods
      | undefined;
    setCanUseLink(!!availablePaymentMethods?.link);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements || isPaymentInFlight() || !validateBeforeConfirm()) {
      event.paymentFailed({ reason: "fail" });
      return;
    }

    setPaymentInFlight(true);

    // Link's own popup shows "Processing…" until paymentFailed()/the confirm
    // promise resolves — an uncaught rejection here (e.g. a misconfigured
    // Elements instance) would leave that UI stuck forever, so any failure
    // from this point on must still resolve through paymentFailed().
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setPaymentInFlight(false);
        event.paymentFailed({ reason: "fail" });
        return;
      }

      const { paymentMethod, error } = await stripe.createPaymentMethod({ elements });
      if (error || !paymentMethod) {
        setPaymentInFlight(false);
        event.paymentFailed({ reason: "fail" });
        return;
      }

      const newPaymentMethod = mapStripePaymentMethodToDTO(paymentMethod);
      dispatch({
        type: ADD_PAYMENT_METHOD,
        paymentMethodId: newPaymentMethod.id,
        view: PaymentMethodManagerView.CHECKOUT,
      });
      onPaymentMethodAttachedRef.current();
    } catch {
      setPaymentInFlight(false);
      event.paymentFailed({ reason: "fail" });
    }
  };

  return { canUseLink, handleReady, handleConfirm };
};

export default useLinkExpressCheckout;
